// Auth0 SPA wrapper. Replaces the old SvelteKit/auth0-js + server-side flow.
// Exposes a singleton @auth0/auth0-spa-js client plus reactive Svelte stores.

import { writable } from 'svelte/store';
import { createAuth0Client } from '@auth0/auth0-spa-js';

const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN;
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID;
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE;
const AUTH0_ORGANIZATION_ID = import.meta.env.VITE_AUTH0_ORGANIZATION_ID;

// Reactive stores
export const user = writable(null);
export const isAuthenticated = writable(false);
export const isLoading = writable(true);

let clientPromise = null;

export function getClient() {
  if (!clientPromise) {
    clientPromise = createAuth0Client({
      domain: AUTH0_DOMAIN,
      clientId: AUTH0_CLIENT_ID,
      authorizationParams: {
        redirect_uri: window.location.origin,
        audience: AUTH0_AUDIENCE,
        ...(AUTH0_ORGANIZATION_ID ? { organization: AUTH0_ORGANIZATION_ID } : {})
      },
      cacheLocation: 'localstorage',
      useRefreshTokens: true
    });
  }
  return clientPromise;
}

export async function handleRedirectCallback() {
  const client = await getClient();
  const search = window.location.search;
  if (search.includes('code=') && search.includes('state=')) {
    try {
      await client.handleRedirectCallback();
    } finally {
      // Strip auth0 query params from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }
}

export async function refreshAuthState() {
  const client = await getClient();
  const authed = await client.isAuthenticated();
  isAuthenticated.set(authed);
  if (authed) {
    user.set(await client.getUser());
  } else {
    user.set(null);
  }
  // NOTE: isLoading is intentionally NOT flipped here. The caller is
  // responsible for setting isLoading=false AFTER any downstream work
  // that pages depend on (e.g. sessionBridge populating the legacy
  // sessionStore). Flipping isLoading too early causes the Router to
  // mount Index.svelte before the legacy store is populated, which
  // makes Index see isAuthenticated=false from sessionStore and
  // redirect to /login.
  return authed;
}

export async function login(returnTo) {
  const client = await getClient();
  await client.loginWithRedirect({
    appState: { returnTo: returnTo || window.location.pathname },
    authorizationParams: {
      redirect_uri: window.location.origin
    }
  });
}

export async function logout() {
  const client = await getClient();
  await client.logout({
    logoutParams: { returnTo: window.location.origin }
  });
}

export async function getToken() {
  const client = await getClient();
  return client.getTokenSilently({
    authorizationParams: { audience: AUTH0_AUDIENCE }
  });
}

export async function getUser() {
  const client = await getClient();
  return client.getUser();
}

export async function checkAuthenticated() {
  const client = await getClient();
  return client.isAuthenticated();
}
